/**
 * API service for Clipper
 */
import axios from 'axios';
import { useAuthStore } from '../store';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use((config) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const hasToken = useAuthStore.getState().token;
    
    // Handle 401 errors
    if (error.response?.status === 401 && hasToken) {
      // Check if it's an admin endpoint - don't immediately logout
      const isAdminEndpoint = error.config?.url?.includes('/admin');
      
      // For admin endpoints, the token might still be valid but user lacks permissions
      if (isAdminEndpoint) {
        console.warn('Admin access denied - may need re-login');
        // Don't auto-logout, let the UI handle it
      } else {
        // For regular endpoints, token is truly invalid
        console.log('Session expired, logging out...');
        useAuthStore.getState().logout();
        // Don't redirect immediately - let the component handle it
      }
    }
    
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (data) => api.post('/auth/signup', data),
  login: (data) => api.post('/auth/login', data),
  // New player OTP-first flow
  startPlayerOTP: (phone) => api.post('/auth/player/start', { phone }),
  verifyPlayerOTP: (phone, code) => api.post('/auth/player/verify', { phone, code }),
  completePlayerProfile: (data) => api.post('/auth/complete-profile', data),
  updateProfile: (data) => api.patch('/auth/update-profile', data),
  sendEmailVerificationOTP: (email) => api.post('/auth/email/send-otp', { email }),
  verifyEmailVerificationOTP: (code) => api.post('/auth/email/verify', { code }),
  sendPasswordRecoveryOTP: (phone) => api.post('/auth/password-recovery/send-otp', { phone }),
  confirmPasswordRecoveryOTP: (data) => api.post('/auth/password-recovery/confirm', data),
  // Legacy endpoints (kept for compatibility)
  verifyPhone: (data) => api.post('/auth/verify-phone', data),
  resendOTP: (userId, verificationType = 'email') => api.post(`/auth/resend-otp?user_id=${userId}&verification_type=${verificationType}`),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
  verifyEmail: (data) => api.post('/auth/verify-email', data),
  getMe: () => api.get('/auth/me'),
  logout: () => api.post('/auth/logout'),
  // Phone verification
  sendPhoneOTP: (phone) => api.post('/auth/send-phone-otp', { phone }),
  verifyPhoneOTP: (phone, code) => api.post('/auth/verify-phone-otp', { phone, code }),
  changePhone: (newPhone, code) => api.post('/auth/change-phone', { new_phone: newPhone, code }),
  getTwilioStatus: () => api.get('/auth/twilio-status'),
};

// Cities & Clubs API
export const clubsAPI = {
  getCities: () => api.get('/cities'),
  getClubs: () => api.get('/clubs'),
  getClub: (clubId) => api.get(`/clubs/${clubId}`),
  getPublicStats: () => api.get('/stats/public'),
};

// QR & Court API
export const courtAPI = {
  validateQR: (data) => api.post('/qr/validate', data),
  joinCourt: (data) => api.post('/court/join', data),
  leaveCourt: () => api.post('/court/leave'),
  getSession: () => api.get('/session'),
};

// Weather API
export const weatherAPI = {
  getWeather: (lat, lng) => api.get('/weather', { params: { lat, lng } }),
};

// Clips API
export const clipsAPI = {
  requestClip: (data) => api.post('/clips/request', data),
  getClip: (clipId) => api.get(`/clips/${clipId}`),
  listClips: () => api.get('/clips'),
  refreshUrl: (clipId) => api.post(`/clips/${clipId}/refresh-url`),
  downloadClip: (clipId) => api.get(`/clips/download/${clipId}`, { responseType: 'blob' }),
  createShortUrl: (clipId) => api.post(`/v/create?clip_id=${clipId}`),
  getStreamUrl: (clipId) => api.get(`/clips/${clipId}/stream`),
};

// Share API
export const shareAPI = {
  createShareLink: (data) => api.post('/share/create', data),
  getSharedClip: (token) => api.get(`/share/${token}`),
};

// Seed data (dev)
export const seedAPI = {
  seed: () => api.post('/seed'),
};

// User Shelly Buttons API
export const userShellyAPI = {
  getMyButtons: () => api.get('/user/shelly-buttons'),
  requestDeactivation: (deviceId, reason) => api.post(`/user/shelly-buttons/${deviceId}/request-deactivation`, { reason }),
};

// Player Stats API
export const playerAPI = {
  getStats: () => api.get('/player/stats'),
};

// FAQ API (Public)
export const faqAPI = {
  getCategories: () => api.get('/faq/categories'),
  getItems: (categorySlug = null) => api.get('/faq/items', { params: { category_slug: categorySlug } }),
  viewItem: (itemId) => api.get(`/faq/items/${itemId}/view`),
  submitQuestion: (data) => api.post('/faq/questions', data),
  getMyQuestions: () => api.get('/faq/my-questions'),
};

// Public API
export const publicAPI = {
  getSupportWhatsapp: () => api.get('/public/support-whatsapp'),
  getClub: (clubId) => api.get(`/clubs/${clubId}`),
};

// Admin API
export const adminAPI = {
  getStats: () => api.get('/admin/stats'),
  getFinancialStats: () => api.get('/admin/financial-stats'),
  getAdoptionStats: () => api.get('/admin/adoption-stats'),
  getUsers: (params) => api.get('/admin/users', { params }),
  getUser: (userId) => api.get(`/admin/users/${userId}`),
  createUser: (data) => api.post('/admin/users', data),
  updateUser: (userId, data) => api.patch(`/admin/users/${userId}`, data),
  deleteUser: (userId) => api.delete(`/admin/users/${userId}`),
  // Camera Management
  getCamerasStatus: () => api.get('/admin/cameras'),
  getCameras: (params) => api.get('/admin/cameras', { params }),
  getAvailableCameras: () => api.get('/admin/cameras/available'),
  getCameraAssignments: () => api.get('/admin/cameras/assignments'),
  getCameraAuditLog: (params) => api.get('/admin/cameras/audit-log', { params }),
  assignCameraToCourtAdmin: (clubId, courtId, hikconnectCameraId) => 
    api.put(`/admin/clubs/${clubId}/courts/${courtId}/camera`, { hikconnect_camera_id: hikconnectCameraId }),
  assignCameraToCourtAPI: (clubId, courtId, cameraId) => api.put(`/admin/clubs/${clubId}/courts/${courtId}/camera`, { hikconnect_camera_id: cameraId }),
  unassignCameraFromCourt: (clubId, courtId, cameraId) => 
    api.delete(`/admin/clubs/${clubId}/courts/${courtId}/camera`, { params: { camera_id: cameraId } }),
  // Clubs management
  getClubs: () => api.get('/admin/clubs'),
  getClub: (clubId) => api.get(`/admin/clubs/${clubId}`),
  createClub: (data) => api.post('/admin/clubs', data),
  updateClub: (clubId, data) => api.patch(`/admin/clubs/${clubId}`, data),
  deleteClub: (clubId) => api.delete(`/admin/clubs/${clubId}`),
  addCourt: (clubId, courtNumber = null) => api.post(`/admin/clubs/${clubId}/courts`, courtNumber ? { court_number: courtNumber } : {}),
  updateCourt: (clubId, courtId, courtNumber) => api.patch(`/admin/clubs/${clubId}/courts/${courtId}`, { court_number: courtNumber }),
  deleteCourt: (clubId, courtId) => api.delete(`/admin/clubs/${clubId}/courts/${courtId}`),
  // QR Generation
  generateQR: (clubId, courtId) => api.get(`/admin/clubs/${clubId}/qr/${courtId}`),
  generateAllQRs: (clubId) => api.get(`/admin/clubs/${clubId}/qr-all`),
  downloadCourtPDF: (clubId, courtId) => api.get(`/admin/clubs/${clubId}/courts/${courtId}/pdf-cancha`, { responseType: 'blob' }),
  listQRCodes: () => api.get('/admin/qr/list'),
  // Seed
  seedClubs: () => api.post('/admin/seed-clubs'),
  // Shelly Button Integration
  getShellyDevices: () => api.get('/admin/shelly/devices'),
  registerShellyDevice: (data) => api.post('/admin/shelly/devices', data),
  updateShellyDevice: (deviceId, data) => api.patch(`/admin/shelly/devices/${deviceId}`, data),
  assignShellyToUser: (deviceId, userId) => api.patch(`/admin/shelly/devices/${deviceId}/assign`, { user_id: userId }),
  unassignShelly: (deviceId) => api.patch(`/admin/shelly/devices/${deviceId}/unassign`),
  deleteShellyDevice: (deviceId) => api.delete(`/admin/shelly/devices/${deviceId}`),
  getDeactivationRequests: () => api.get('/admin/shelly/deactivation-requests'),
  approveDeactivation: (deviceId) => api.patch(`/admin/shelly/devices/${deviceId}/approve-deactivation`),
  testShellyWebhook: (deviceId, eventType) => api.post(`/shelly/webhook/${deviceId}`, { event: eventType }),
  testShellyAdminMode: (deviceId, eventType, targetUserId) => api.post(`/admin/shelly/devices/${deviceId}/test-mode`, { event_type: eventType, target_user_id: targetUserId }),
  // Shelly Signal Testing & Logs
  testShellySignal: (deviceId) => api.post(`/admin/shelly/devices/${deviceId}/test-signal`),
  getShellySignalLogs: (deviceId, limit = 20) => api.get(`/admin/shelly/devices/${deviceId}/signal-logs`, { params: { limit } }),
  getAllShellySignalLogs: (limit = 50) => api.get('/admin/shelly/signal-logs', { params: { limit } }),
  // Shelly User Search & Create
  searchUsersForShelly: (query) => api.post('/admin/shelly/search-users', query),
  createUserForShelly: (data) => api.post('/admin/shelly/create-user', data),
  // Watermark Management
  getWatermarks: () => api.get('/admin/watermarks'),
  uploadWatermark: (data) => api.post('/admin/watermarks/upload', data),
  updateWatermark: (position, data) => api.patch(`/admin/watermarks/${position}`, data),
  deleteWatermark: (position) => api.delete(`/admin/watermarks/${position}`),
  // System Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.patch('/admin/settings', data),
  testHikConnect: () => api.post('/admin/settings/test-hikconnect'),
  testResend: () => api.post('/admin/settings/test-resend'),
  getActivityLog: (limit = 50) => api.get(`/admin/settings/activity-log?limit=${limit}`),
  // E-commerce Admin
  getShopOrders: (params) => api.get('/admin/shop/orders', { params }),
  updateOrderStatus: (orderId, data) => api.patch(`/admin/shop/orders/${orderId}/status`, data),
  getShopStats: () => api.get('/admin/shop/stats'),
  getSliderImages: () => api.get('/admin/shop/slider-images'),
  updateSliderImage: (data) => api.post('/admin/shop/slider-images', data),
  deleteSliderImage: (position) => api.delete(`/admin/shop/slider-images/${position}`),
  // Shelly Blacklist
  getBlacklist: () => api.get('/admin/shelly/blacklist'),
  addToBlacklist: (data) => api.post('/admin/shelly/blacklist', data),
  removeFromBlacklist: (deviceId) => api.delete(`/admin/shelly/blacklist/${deviceId}`),
  // FAQ Management
  getFAQCategories: () => api.get('/admin/faq/categories'),
  createFAQCategory: (data) => api.post('/admin/faq/categories', data),
  deleteFAQCategory: (categoryId) => api.delete(`/admin/faq/categories/${categoryId}`),
  getFAQItems: (categorySlug = null) => api.get('/admin/faq/items', { params: { category_slug: categorySlug } }),
  createFAQItem: (data) => api.post('/admin/faq/items', data),
  updateFAQItem: (itemId, data) => api.patch(`/admin/faq/items/${itemId}`, data),
  deleteFAQItem: (itemId) => api.delete(`/admin/faq/items/${itemId}`),
  // User Questions Management
  getUserQuestions: (status = null) => api.get('/admin/faq/questions', { params: { status } }),
  respondToQuestion: (questionId, data) => api.post(`/admin/faq/questions/${questionId}/respond`, data),
  updateQuestionStatus: (questionId, status) => api.patch(`/admin/faq/questions/${questionId}/status?status=${status}`),
  // FAQ AI Features
  generateAIResponse: (questionId) => api.post(`/admin/faq/questions/${questionId}/generate-ai-response`),
  sendEmailResponse: (questionId, data) => api.post(`/admin/faq/questions/${questionId}/send-email-response`, data),
  checkQuestionSimilarity: (questionId) => api.post(`/admin/faq/questions/${questionId}/check-similarity`),
  getSimilarityGroups: () => api.get('/admin/faq/questions/similarity-groups'),
  createFAQFromGroup: (data) => api.post('/admin/faq/questions/create-from-group', data),
  // Bitácora AI Analysis
  analyzeBitacora: () => api.post('/admin/bitacora/analyze'),
  getBitacoraAnalysisHistory: (limit = 10) => api.get('/admin/bitacora/analysis-history', { params: { limit } }),
  // Admin Notifications
  getNotifications: (unreadOnly = false) => api.get('/admin/notifications', { params: { unread_only: unreadOnly } }),
  markNotificationRead: (notificationId) => api.patch(`/admin/notifications/${notificationId}/read`),
  markAllNotificationsRead: () => api.post('/admin/notifications/mark-all-read'),
  deleteNotification: (notificationId) => api.delete(`/admin/notifications/${notificationId}`),
  // Delete test users
  deleteTestUsers: () => api.delete('/admin/users/test-users'),
  // Hik-Connect Diagnostics & Camera Testing
  getHikconnectDiagnostics: () => api.get('/admin/hikconnect/diagnostics'),
  testCamera: (data) => api.post('/admin/hikconnect/test-camera', data),
};

// E-commerce / Shop API
export const shopAPI = {
  getProducts: () => api.get('/shop/products'),
  getSliderImages: () => api.get('/shop/slider-images'),
  createCheckout: (data) => api.post('/shop/checkout', data),
  getOrder: (orderId) => api.get(`/shop/order/${orderId}`),
  getUserOrders: () => api.get('/user/orders'),
};

// POS (Point of Sale) API
export const posAPI = {
  // Departments
  getDepartments: () => api.get('/pos/departments'),
  createDepartment: (data) => api.post('/pos/departments', data),
  updateDepartment: (id, data) => api.patch(`/pos/departments/${id}`, data),
  deleteDepartment: (id) => api.delete(`/pos/departments/${id}`),
  
  // Categories
  getCategories: (departmentId = null) => api.get('/pos/categories', { params: { department_id: departmentId } }),
  createCategory: (data) => api.post('/pos/categories', data),
  deleteCategory: (id) => api.delete(`/pos/categories/${id}`),
  
  // Products
  getProducts: (params = {}) => api.get('/pos/products', { params }),
  getProduct: (id) => api.get(`/pos/products/${id}`),
  createProduct: (data) => api.post('/pos/products', data),
  updateProduct: (id, data) => api.patch(`/pos/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/pos/products/${id}`),
  adjustStock: (productId, data) => api.post(`/pos/products/${productId}/stock`, data),
  getLowStock: () => api.get('/pos/products/low-stock'),
  
  // Cash Sessions
  getCurrentSession: () => api.get('/pos/cash-session'),
  openSession: (data) => api.post('/pos/cash-session/open', data),
  closeSession: (data) => api.post('/pos/cash-session/close', data),
  getSessionHistory: (params = {}) => api.get('/pos/cash-session/history', { params }),
  
  // Sales
  createSale: (data) => api.post('/pos/sales', data),
  getSales: (params = {}) => api.get('/pos/sales', { params }),
  getSale: (id) => api.get(`/pos/sales/${id}`),
  refundSale: (id, data) => api.post(`/pos/sales/${id}/refund`, data),
  
  // POS Users (Operators)
  getUsers: () => api.get('/pos/users'),
  createUser: (data) => api.post('/pos/users', data),
  loginUser: (data) => api.post('/pos/users/login', data),
  updateUser: (id, data) => api.patch(`/pos/users/${id}`, data),
  deleteUser: (id) => api.delete(`/pos/users/${id}`),
  
  // Reports
  getSalesReport: (startDate, endDate) => api.get('/pos/reports/sales', { params: { start_date: startDate, end_date: endDate } }),
  getInventoryReport: () => api.get('/pos/reports/inventory'),
  getStockMovements: (params = {}) => api.get('/pos/reports/stock-movements', { params }),
  
  // Inventory Entry/Exit
  createInventoryEntry: (data) => api.post('/pos/inventory/entry', data),
  createInventoryExit: (data) => api.post('/pos/inventory/exit', data),
  getInventoryTransactions: (params = {}) => api.get('/pos/inventory/transactions', { params }),
  getInventoryMovements: (params = {}) => api.get('/pos/inventory/movements', { params }),
};

// Playtomic API (Mock Integration)
export const playtomicAPI = {
  // Status
  getStatus: () => api.get('/playtomic/status'),
  
  // Reservations
  getReservations: (params = {}) => api.get('/playtomic/reservations', { params }),
  getHistory: (params = {}) => api.get('/playtomic/reservations/history', { params }),
  processPayment: (reservationId, data) => api.post(`/playtomic/reservations/${reservationId}/process-payment`, data),
  
  // Blocked Slots
  getBlockedSlots: (params = {}) => api.get('/playtomic/blocked-slots', { params }),
  blockSlot: (data) => api.post('/playtomic/blocked-slots', data),
  unblockSlot: (slotId) => api.delete(`/playtomic/blocked-slots/${slotId}`),
  
  // Courts
  getCourts: () => api.get('/playtomic/courts'),
  
  // Sync
  sync: () => api.post('/playtomic/sync'),
};

export default api;
